
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EpisodeWithCategory, EpisodeCategory, EpisodeInsert } from "./types";
import { EpisodeFormFields } from "./components/EpisodeFormFields";
import { EpisodeFormActions } from "./components/EpisodeFormActions";
import { validateEpisodeForm } from "./validation/EpisodeFormValidation";

interface EpisodeFormProps {
  episode?: EpisodeWithCategory;
  categories: EpisodeCategory[];
  onSave: (episode: EpisodeInsert) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

export const EpisodeForm = ({ episode, categories, onSave, onCancel, saving }: EpisodeFormProps) => {
  // Determine initial video platform based on existing data
  const getInitialVideoPlatform = () => {
    if (episode?.youtube_id) return "youtube";
    if (episode?.vimeo_id) return "vimeo";
    return undefined;
  };

  const [formData, setFormData] = useState<EpisodeInsert>({
    title: episode?.title || "",
    description: episode?.description || "",
    thumbnail_url: episode?.thumbnail_url || "",
    duration: episode?.duration || "",
    youtube_id: episode?.youtube_id || "",
    vimeo_id: episode?.vimeo_id || "",
    category_id: episode?.category_id || "",
    status: episode?.status || "draft",
    featured: episode?.featured || false,
    video_platform: getInitialVideoPlatform(),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const handleFieldChange = (field: keyof EpisodeInsert, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Clear opposite video ID when platform changes
      if (field === 'video_platform') {
        if (value === 'youtube') {
          newData.vimeo_id = "";
        } else if (value === 'vimeo') {
          newData.youtube_id = "";
        }
      }
      
      return newData;
    });
  };

  const handleErrorClear = (field: string) => {
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || saving) {
      console.log("Form submission already in progress");
      return;
    }

    const validationErrors = validateEpisodeForm(formData);
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      console.log("Form validation failed:", validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      console.log("Form submitting with data:", formData);
      await onSave(formData);
      console.log("Form submission completed successfully");
    } catch (error) {
      console.error("Form submission failed:", error);
      // Error is already handled by the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isSubmitting || saving;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{episode ? "Edit Episode" : "Create New Episode"}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <EpisodeFormFields
            formData={formData}
            categories={categories}
            errors={errors}
            isLoading={isLoading}
            onFieldChange={handleFieldChange}
            onErrorClear={handleErrorClear}
          />
          <EpisodeFormActions
            onCancel={onCancel}
            isLoading={isLoading}
          />
        </form>
      </CardContent>
    </Card>
  );
};
